"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useFormatter } from "@/hooks/use-formatter"
import {
  IconAlertTriangle,
  IconCheck,
  IconLoader2,
  IconPhone,
  IconPlus,
  IconSearch,
  IconStethoscope,
  IconX,
} from "@tabler/icons-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { showAppointmentWarnings } from "@/components/calendar/appointment-warnings";
import { ConflictOverridePanel } from "@/components/appointments/conflict-override-dialog";
import { SlotGrid } from "@/components/appointments/slot-grid";
import { categoryIcon, getCategoryLabel } from "@/lib/appointment-categories";
import { canOpenAppointmentEditor } from "@/lib/appointment-creation-access";
import { AppointmentCreationUnavailable } from "@/components/appointments/appointment-creation-unavailable";
import { formField } from "@/lib/appointment-presets";
import { getConflictPayload, type AppointmentConflictPayload } from "@/lib/appointments-api";
import { getLeadsForPicker, getLead, createLead, type LeadReference } from "@/lib/leads-api";
// WP9: createAppointment/updateAppointment always preserve the additive
// `warnings` array (room_conflict / blockout_restricted) so the form can
// surface it (pathfinder U4: the warning-discarding variants were deleted).
import {
  createAppointment,
  getAppointmentAssignableUsers,
  updateAppointment,
} from "@/lib/appointments-api";
import { getCalendarDoctors } from "@/lib/blockouts-api";
import { getDoctors } from "@/lib/doctors-api";
import { getRooms } from "@/lib/rooms-api";
import type { CalendarRoom } from "@/components/calendar/calendar-types";
import { getAppointmentCategories } from "@/lib/appointment-workflow-api";
import {
  createLeadVisit,
  getLeadVisits,
  getVisitPurposes,
} from "@/lib/clinical-visits-api";
import { useAuthStore } from "@/stores/auth-store";
import { usePermissions } from "@/hooks/use-permissions";
import type {
  Appointment,
  AppointmentType,
  ClinicalVisit,
  CreateAppointmentData,
  UpdateAppointmentData,
  Lead,
  User,
  VisitPurposesMap,
} from "@/types";

// Apollo deposit-modal design language — shared field styling.
const LABEL_CLS =
  "text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground";
const FIELD_CLS =
  "rounded-[10px] bg-muted/40 focus:border-primary/50 focus:ring-[3px] focus:ring-primary/15";

const DURATION_OPTIONS = [
  { value: 15, labelKey: "appointmentForm.durations.min15" },
  { value: 30, labelKey: "appointmentForm.durations.min30" },
  { value: 45, labelKey: "appointmentForm.durations.min45" },
  { value: 60, labelKey: "appointmentForm.durations.hr1" },
  { value: 90, labelKey: "appointmentForm.durations.hr1_5" },
  { value: 120, labelKey: "appointmentForm.durations.hr2" },
  { value: 180, labelKey: "appointmentForm.durations.hr3" },
  { value: 240, labelKey: "appointmentForm.durations.hr4" },
];

const buildAppointmentSchema = (t: (k: string) => string) =>
  z.object({
    lead_id: z.string().min(1, t("appointmentForm.validation.leadRequired")),
    // 85-03: an optional note on the calendar — the display title is derived.
    title: z.string().max(255).optional(),
    description: z.string().max(2000).optional(),
    scheduled_date: z.string().min(1, t("appointmentForm.validation.dateRequired")),
    scheduled_time: z.string().min(1, t("appointmentForm.validation.timeRequired")),
    duration_minutes: z.number().min(15).max(480),
    // Phase 79 (UI-SPEC §6b): config-driven categories — server validates
    // membership via Rule::in(config('appointment_categories.list')).
    category: z.string().min(1),
    user_id: z.string().optional(),
    // ArtDent 261005: the doctors besides the main one (user ids as strings,
    // like user_id). Sent only where the clinic books co-doctors.
    co_doctor_ids: z.array(z.string()).optional(),
    // Visits Redesign: optional grouping into a clinical visit ("" = none).
    clinical_visit_id: z.string().optional(),
  });

const NO_VISIT = "__none__";
const NEW_VISIT = "__new__";

// Frontend-only auto-suggestion: prefer the visit whose travel window contains
// the date, else the visit whose linked-appointment span is within ±3 days.
// All wire values are YYYY-MM-DD, so the travel window compares lexically and
// the ±3-day window parses uniformly as LOCAL midnight — parsing date-only
// strings via new Date(s) would be UTC midnight and miss the arrival day in
// UTC+ timezones.
//
// Visit Purpose v2 follow-up (260819): visits are born at the call centre
// BEFORE the desk books the appointment, so a Visit-type appointment should
// come out linked without the coordinator having to remember the picker. Two
// fallback tiers cover the dates-don't-line-up-yet case: a lead with exactly
// one visit gets that visit regardless of dates, and otherwise the visit whose
// window sits nearest the chosen date (within NEAREST_VISIT_MAX_DAYS) wins.
const NEAREST_VISIT_MAX_DAYS = 14;

function computeSuggestedVisit(dateStr: string, visits: ClinicalVisit[]): string | null {
  if (dateStr) {
    for (const visit of visits) {
      const arrival = visit.travel?.arrival_date;
      const departure = visit.travel?.departure_date;
      if (arrival && departure && dateStr >= arrival && dateStr <= departure) {
        return visit.id;
      }
    }

    const target = new Date(`${dateStr}T00:00:00`);
    if (!Number.isNaN(target.getTime())) {
      for (const visit of visits) {
        const from = visit.date_range?.from;
        const to = visit.date_range?.to;
        if (from && to) {
          const lo = new Date(`${from}T00:00:00`);
          lo.setDate(lo.getDate() - 3);
          const hi = new Date(`${to}T00:00:00`);
          hi.setDate(hi.getDate() + 3);
          if (target >= lo && target <= hi) {
            return visit.id;
          }
        }
      }
    }
  }

  // One visit on file → there is nothing to choose; link it outright when no
  // date is picked yet or the visit carries no dates at all (a fresh
  // call-centre visit). With both a date and a window, the distance cap
  // below still applies — a trip from months ago is not this appointment.
  if (visits.length === 1) {
    const only = visits[0];
    const from = only.travel?.arrival_date ?? only.date_range?.from;
    if (!dateStr || !from) {
      return only.id;
    }
  }

  // Pick the visit whose window lies nearest the chosen date, but never
  // bridge more than NEAREST_VISIT_MAX_DAYS — auto-linking a trip from
  // months ago would be worse than leaving the picker empty.
  if (dateStr) {
    const target = new Date(`${dateStr}T00:00:00`).getTime();
    if (!Number.isNaN(target)) {
      let bestId: string | null = null;
      let bestDistance = NEAREST_VISIT_MAX_DAYS * 86_400_000;
      for (const visit of visits) {
        const from = visit.travel?.arrival_date ?? visit.date_range?.from;
        const to = visit.travel?.departure_date ?? visit.date_range?.to ?? from;
        if (!from || !to) continue;
        const lo = new Date(`${from}T00:00:00`).getTime();
        const hi = new Date(`${to}T00:00:00`).getTime();
        if (Number.isNaN(lo) || Number.isNaN(hi)) continue;
        const distance = target < lo ? lo - target : target > hi ? target - hi : 0;
        if (distance <= bestDistance) {
          bestDistance = distance;
          bestId = visit.id;
        }
      }
      return bestId;
    }
  }

  return null;
}

type AppointmentFormValues = z.infer<ReturnType<typeof buildAppointmentSchema>>;

/**
 * 86-01: what a caller knows before the sheet opens in create mode — the
 * five `preselected*` props of the old dialog collapsed into one object.
 */
export interface AppointmentDraft {
  leadId?: string;
  /** Doctor-day / rooms slot click: pre-assign this staff member. */
  userId?: string;
  /** Slot / day click: prefill date + time. */
  date?: Date;
  /** Visits Redesign: pre-link a clinical visit. */
  visitId?: string;
  /** Rooms / Salla slot click: pre-set the treatment room. */
  roomId?: string;
  /**
   * Initial intent, so a "Log a call" affordance can open pre-set to Call.
   * Only takes effect when typed_events_enabled is on.
   */
  type?: AppointmentType;
  /**
   * Treatment steps this booking is FOR (the desk board's Book on a step row).
   * Sent as `stage_ids`, which links each step to the new appointment — the
   * link the board's step rows clear on. Dropped if the patient is changed in
   * the editor: the steps belong to the preselected patient only.
   */
  stageIds?: string[];
  /**
   * Phase 89 — the doctor assigned to the treatment those steps belong to.
   * Prefills the assignee (prefill + warn, never lock — the Phase 82 D-3
   * posture): the desk books with someone else the day that doctor is off.
   */
  stageDoctorId?: number | null;
}

const EMPTY_DRAFT: AppointmentDraft = {};

export interface AppointmentEditorProps {
  /** Present ⇒ edit mode; absent ⇒ create mode seeded from `draft`. */
  appointment?: Appointment;
  draft?: AppointmentDraft;
  onSaved: (appointment: Appointment) => void;
  onCancel: () => void;
  /** Unsaved-changes guard hook for the host sheet. */
  onDirtyChange?: (dirty: boolean) => void;
  /** The host header mirrors the picked patient in create mode. */
  onLeadChange?: (lead: Lead | null) => void;
}

/**
 * The appointment sheet's EDIT body (86-01). Mounted only while editing, so
 * every "on open" effect below runs on mount; the host sheet owns the chrome
 * (header, status, check-in, delete) and this owns the fields + save.
 */
export function AppointmentEditor(props: AppointmentEditorProps) {
  const user = useAuthStore((state) => state.user);
  if (!canOpenAppointmentEditor({
    editing: Boolean(props.appointment),
    presets: user?.calendar_presets,
    typedEventsEnabled: user?.feature_flags?.typed_events_enabled === true,
  })) {
    return <AppointmentCreationUnavailable onClose={props.onCancel} />;
  }
  return <AppointmentEditorFields {...props} />;
}

function AppointmentEditorFields({
  appointment,
  draft = EMPTY_DRAFT,
  onSaved,
  onCancel,
  onDirtyChange,
  onLeadChange,
}: AppointmentEditorProps) {
  const preselectedLeadId = draft.leadId;
  const preselectedUserId = draft.userId;
  const preselectedDate = draft.date;
  const preselectedVisitId = draft.visitId;
  const preselectedRoomId = draft.roomId;
  const preselectedStageIds = draft.stageIds;
  const preselectedStageDoctorId = draft.stageDoctorId ?? null;
  const defaultType: AppointmentType = draft.type ?? "clinic_visit";
  const t = useTranslations("appointments");
  const tc = useTranslations("common");
  // WP9: title for the additive-warnings toast (room over-capacity / blockout).
  const warningTitle = t("appointmentForm.toast.warningTitle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 84-05: a 409 (doctor/patient double-booking) opens the override dialog;
  // "Book anyway" stores the reason here and re-runs the submit once.
  const [conflict, setConflict] = useState<AppointmentConflictPayload | null>(null);
  const overrideReasonRef = useRef<string | null>(null);
  // Phase 45 (D-10, RBAC-14): picker results are LeadReference (4 fields only).
  // selectedLead still stores the full Lead — we fetch it via getLead(id) after
  // selection so downstream reads of lead.full_name / lead.email / etc. keep working.
  const [leads, setLeads] = useState<LeadReference[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const leadPickerRef = useRef<HTMLDivElement>(null);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [agents, setAgents] = useState<User[]>([]);
  // Doctor Profiles enrichment: id → {color, specialty} for doctors that have a
  // profile. Empty (today's plain rendering) when the flag is off, the fetch
  // fails/403s/404s, or no doctor has a profile yet.
  const [doctorMeta, setDoctorMeta] = useState<
    Map<number, { color: string | null; specialty: string | null }>
  >(new Map());
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [quickForm, setQuickForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const isEditing = !!appointment;

  // Per-mode write gating (HIDE, never disable): create mode needs
  // appointments.create, edit mode's save needs appointments.edit. When absent,
  // the submit button is not rendered — the dialog becomes a read-only view.
  const { can } = usePermissions();
  const canSubmit = isEditing ? can("appointments.edit") : can("appointments.create");

  // Appointments rebuild 88: the four per-field flags collapsed into
  // `user.appointment_presets.form` (see lib/appointment-presets.ts).
  const appointmentPresets = useAuthStore((state) => state.user?.appointment_presets);
  const quickClientEnabled = formField(appointmentPresets, "quick_client") === "shown";
  // lead_phone_optional_enabled clinics (Dential 260924) book walk-ins by name
  // alone — the backend already accepts a phone-less lead there.
  const quickClientPhoneOptional = useAuthStore(
    (state) => state.user?.feature_flags?.lead_phone_optional_enabled ?? false
  );
  const categoryEnabled = formField(appointmentPresets, "category") === "shown";
  const hideAssigneeEnabled = formField(appointmentPresets, "assignee") === "hidden";
  const hideDurationEnabled = formField(appointmentPresets, "duration") === "hidden";
  // Visits Redesign: fail-closed flag gate for the Visit field.
  const visitCategorizationEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.visit_categorization_enabled ?? false
  );
  // Per-role trim from the login presets (hide_visit_field). A desk can book
  // chairs without running the clinical visit grouping, so the picker comes off
  // for that role while the clinic-wide flag stays on for everyone else.
  // Fail-open — absent means "show", so no clinic loses the field silently.
  const hideVisitField = useAuthStore(
    (state) => state.user?.calendar_presets?.hide_visit_field ?? false
  );
  // Everything downstream keys off THIS, not the raw flag: hiding the field has
  // to keep it out of the fetch and the submit payload too, or a field nobody
  // saw could still create a visit.
  const visitFieldEnabled = visitCategorizationEnabled && !hideVisitField;
  // typed_events_enabled: this clinic books calls as well as visits, and the
  // type visuals follow it. Fail-closed — off ⇒ the form renders and submits
  // exactly as before (server defaults the type by role). On ⇒ a 2-option
  // Visit/Call segmented control is shown.
  const eventTypesEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.typed_events_enabled === true
  );
  // Per-role create intent from the login presets (config/calendar.php,
  // per-clinic overridable). default_create_type beats the caller's defaultType
  // — clinics that set it don't run the lens-driven quickCreateType, and on
  // clinics that don't set it (null) the caller's value wins as before.
  // lock_create_type removes the Visit/Call toggle: create is pinned to the
  // resolved default, edit still seeds from the appointment's REAL type so
  // opening the other kind is a no-op, never a silent conversion.
  const presetCreateType = useAuthStore(
    (state) => state.user?.calendar_presets?.default_create_type ?? null
  );
  // typed_events_enabled off (the walk-in posture): the clinic never books
  // calls, so the intent is pinned to Visit for every role — the same static
  // chip lock_create_type renders. Edit still seeds from the row's real type
  // (a call someone made before the flag flipped stays a call).
  const presetTypeLocked = useAuthStore(
    (state) => state.user?.calendar_presets?.lock_create_type ?? false
  );
  const typeLocked = presetTypeLocked || !eventTypesEnabled;
  const effectiveDefaultType: AppointmentType = presetTypeLocked && presetCreateType
    ? presetCreateType
    : !eventTypesEnabled
    ? "clinic_visit"
    : (presetCreateType ?? (defaultType === "lead_call" ? "lead_call" : "clinic_visit"));
  // Selected intent for the segmented control. `internal` is never a UI option —
  // it collapses to Visit for display so misclassified rows still edit cleanly.
  const [selectedType, setSelectedType] = useState<AppointmentType>(effectiveDefaultType);
  // Call intent hides visit-only sections (visit categorization, confirmation +
  // check-in timeline). Only meaningful while the flag is on.
  const isCallType = selectedType === "lead_call";
  // Agents always self-assign (backend forces it too) — the Assigned To picker
  // is hidden entirely for them and no user_id is sent.
  const isAgent = useAuthStore(
    (state) => state.user?.roles?.includes("agent") ?? false
  );
  // Rooms / Salla: a visible room picker for Visit intent. Flag read via the
  // defensive cast (rooms_enabled isn't on the FeatureFlags interface yet —
  // same pattern as calendar/page.tsx).
  const roomsEnabled = useAuthStore(
    (state) =>
      (state.user?.feature_flags as { rooms_enabled?: boolean } | undefined)?.rooms_enabled ??
      false
  );
  const showRoomPicker = eventTypesEnabled && roomsEnabled && selectedType === "clinic_visit";
  const [rooms, setRooms] = useState<CalendarRoom[]>([]);
  // "" = automatic (create: backend auto-assigns; edit: no room / Pa sallë).
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  // Config-driven appointment categories (§6b) — fallback preserves the
  // pre-Phase-79 klea|other rendering while the cached fetch resolves.
  const [categories, setCategories] = useState<string[]>(["checkup", "other"]);
  // Visits Redesign: the selected lead's visits + inline "new visit" builder.
  const [leadVisits, setLeadVisits] = useState<ClinicalVisit[]>([]);
  const [visitPurposesMap, setVisitPurposesMap] = useState<VisitPurposesMap>({});
  const [visitMode, setVisitMode] = useState<"existing" | "new">("existing");
  const [newVisitPurpose, setNewVisitPurpose] = useState("");
  const [userChoseVisit, setUserChoseVisit] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  // The visit id the suggestion effect auto-applied (never a user pick) — lets
  // us clear the value when the suggestion evaporates without touching
  // explicit selections.
  const autoAppliedVisitRef = useRef<string | null>(null);
  // Phase 82 — the assignee the care-team prefill applied (never a user pick),
  // mirroring autoAppliedVisitRef. Lets us replace our own suggestion when the
  // lead changes without ever overwriting a deliberate choice.
  const autoAppliedDoctorRef = useRef<string | null>(null);
  const format = useFormatter();
  const tv = useTranslations("visitCategorization");

  const schema = useMemo(() => buildAppointmentSchema(t), [t]);

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      lead_id: "",
      title: "",
      description: "",
      scheduled_date: "",
      scheduled_time: "",
      duration_minutes: 60,
      category: "checkup",
      clinical_visit_id: "",
    },
  });

  const currentUser = useAuthStore((s) => s.user);

  // Config-driven category list — cached module-level, cheap to refetch per open.
  useEffect(() => {
    getAppointmentCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  // The create-mode reset seeds category from the PREVIOUS categories state
  // (the fetch above races it) — once the real list lands, snap an
  // out-of-list value to the list head so the submit can't 422.
  useEffect(() => {
    const current = form.getValues("category");
    if (categories.length > 0 && current && !categories.includes(current)) {
      form.setValue("category", categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);


  // Rooms / Salla: room options for the Visit-intent picker. Fetched once per
  // open; only active rooms come back from GET /rooms.
  useEffect(() => {
    if (!roomsEnabled || !eventTypesEnabled) return;
    getRooms()
      .then(setRooms)
      .catch(() => {});
  }, [roomsEnabled, eventTypesEnabled]);

  // Fetch agents for the user picker when dialog opens. Receptionists lack
  // users.view (GET /users 403s) — on doctor-day clinics fall back to the
  // doctor directory (gated by appointments.view) so the assignee picker
  // still offers the day-view columns' doctors.
  const doctorDayViewEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.doctor_board_enabled ?? false
  );
  // The assignee population mirrors the server's assignableUsers() keying:
  // explicit Visit intent, or (event types off) a doctor-day clinic — where
  // appointments are clinic visits performed by doctors, not agent calls.
  const assigneesAreDoctors = eventTypesEnabled
    ? selectedType === "clinic_visit"
    : doctorDayViewEnabled;
  // ArtDent 261005 — "Other doctors" under the Doctor picker: a visit can be
  // worked by more than one doctor. Only where the picker lists doctors.
  const coDoctorsEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.appointment_co_doctors_enabled ?? false
  );
  const showCoDoctors =
    coDoctorsEnabled && !hideAssigneeEnabled && !isAgent && assigneesAreDoctors && !isCallType;
  // Doctor Profiles picker enrichment (flag default TRUE — deliberate). Read the
  // flag so we skip the extra fetch entirely when a clinic has opted out.
  const doctorProfilesEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.doctor_profiles_enabled ?? true
  );
  useEffect(() => {
    // Agents never see the picker — skip the fetch entirely. Same when the
    // clinic hides the assignee (hide_appointment_assignee_enabled): the
    // fetch below blanks user_id when the booking user is not in the
    // assignable population (admins never are), and with no picker to
    // re-choose from the submit 422'd with "user id must be an integer".
    if (isAgent || hideAssigneeEnabled) return;
    // Intent-keyed list: visits are assigned to doctors, calls to agents
    // (team-scoped server-side for team leads); falls back to the doctor
    // directory for roles that can't create appointments on doctor-day
    // clinics. When event types are off the server defaults to the agent
    // list (legacy behavior).
    getAppointmentAssignableUsers(eventTypesEnabled ? selectedType : undefined)
      .then((res) => {
        const users = res.data as unknown as User[]
        setAgents(users)
        // A type switch swaps the whole population — drop a selection that
        // is no longer offerable so the submit can't 422.
        const current = form.getValues("user_id")
        if (current && !users.some((u) => String(u.id) === String(current))) {
          form.setValue("user_id", "")
        }
      })
      .catch(() => {
        if (!doctorDayViewEnabled) return;
        getCalendarDoctors()
          .then((doctors) =>
            setAgents(
              doctors.map(
                (doctor) =>
                  ({
                    id: doctor.id,
                    full_name: doctor.full_name,
                  } as unknown as User)
              )
            )
          )
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorDayViewEnabled, isAgent, hideAssigneeEnabled, eventTypesEnabled, selectedType]);

  // Fetch the doctor directory alongside the users fetch (parallel, non-blocking)
  // and build an id → {color, specialty} map from doctors that have a profile.
  // Degrades gracefully: the endpoint is behind the doctor_profiles_enabled flag
  // (404 when off) and doctors.view permission (403 when missing) — either way we
  // catch and fall back to today's plain full_name rendering.
  useEffect(() => {
    if (!doctorProfilesEnabled) return;
    getDoctors({ per_page: 100 })
      .then((res) => {
        const map = new Map<
          number,
          { color: string | null; specialty: string | null }
        >();
        res.data.forEach((doctor) => {
          if (doctor.doctor_profile) {
            map.set(doctor.id, {
              color: doctor.doctor_profile.calendar_color,
              specialty: doctor.doctor_profile.specialty,
            });
          }
        });
        setDoctorMeta(map);
      })
      .catch(() => null);
  }, [doctorProfilesEnabled]);

  // Debounced server-side lead search — only when picker is open
  useEffect(() => {
    if (!leadSearchOpen) return;
    const timer = setTimeout(async () => {
      setIsLoadingLeads(true);
      try {
        const response = await getLeadsForPicker({
          context: "appointment",
          q: leadSearchQuery || undefined,
          per_page: 20,
        });
        setLeads(response.data);
      } catch (error) {
        console.error("Failed to load leads", error);
      } finally {
        setIsLoadingLeads(false);
      }
    }, leadSearchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [leadSearchOpen, leadSearchQuery]);

  // Reset form when appointment changes
  useEffect(() => {
    if (appointment) {
      const scheduledAt = new Date(appointment.scheduled_at);
      form.reset({
        lead_id: appointment.lead_id,
        title: appointment.title ?? "",
        description: appointment.description || "",
        scheduled_date: `${scheduledAt.getFullYear()}-${String(scheduledAt.getMonth() + 1).padStart(2, "0")}-${String(scheduledAt.getDate()).padStart(2, "0")}`,
        scheduled_time: scheduledAt.toTimeString().slice(0, 5),
        duration_minutes: appointment.duration_minutes,
        category: appointment.category || categories[0] || "checkup",
        user_id: String(appointment.user_id),
        co_doctor_ids: (appointment.co_doctors ?? []).map((doctor) => String(doctor.id)),
        clinical_visit_id: appointment.clinical_visit_id ?? "",
      });
      if (appointment.lead) {
        setSelectedLead(appointment.lead as unknown as Lead);
      }
      // Calendar redesign (Wave 1): pre-select the control from the saved type;
      // null/internal collapse to Visit so staff can re-type misclassified rows.
      const seededType: AppointmentType =
        appointment.type ?? "clinic_visit";
      setSelectedType(seededType);
      seededTypeRef.current = seededType;
      const seededRoom = appointment.room_id ?? "";
      setSelectedRoomId(seededRoom);
      seededRoomRef.current = seededRoom;
    } else {
      const initialDay = preselectedDate ?? new Date();
      form.reset({
        lead_id: preselectedLeadId || "",
        title: "",
        description: "",
        scheduled_date: `${initialDay.getFullYear()}-${String(initialDay.getMonth() + 1).padStart(2, "0")}-${String(initialDay.getDate()).padStart(2, "0")}`,
        scheduled_time: preselectedDate
          ? preselectedDate.toTimeString().slice(0, 5)
          : "",
        duration_minutes: 60,
        category: categories[0] || "checkup",
        user_id:
          preselectedUserId ??
          (currentUser?.id ? String(currentUser.id) : ""),
        co_doctor_ids: [],
        clinical_visit_id: preselectedVisitId ?? "",
      });
      // Note: preselectedLeadId lookup intentionally omitted from deps — leads array
      // must not be a dependency here or it will reset selectedLead after every fetch.
      setSelectedLead(null);
      // Calendar redesign (Wave 1): create mode starts from the role preset
      // when one is set, else the caller's default.
      setSelectedType(effectiveDefaultType);
      seededTypeRef.current = effectiveDefaultType;
      // Rooms: seed from a rooms-column slot click; "" = automatic assignment.
      setSelectedRoomId(preselectedRoomId ?? "");
      seededRoomRef.current = preselectedRoomId ?? "";
    }
    setShowQuickCreate(false);
    setQuickForm({ first_name: "", last_name: "", phone: "", email: "" });
    // Visits Redesign: reset the visit builder; in edit mode the saved link is
    // treated as an explicit choice so the auto-suggestion never overrides it.
    setVisitMode("existing");
    setNewVisitPurpose("");
    setLeadVisits([]);
    setSuggestionDismissed(false);
    setUserChoseVisit(!!appointment?.clinical_visit_id || !!preselectedVisitId);
    autoAppliedVisitRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment, preselectedLeadId, preselectedUserId, preselectedDate, preselectedVisitId, effectiveDefaultType, preselectedRoomId]);

  // Resolve preselectedLeadId to a lead object on mount
  useEffect(() => {
    if (!preselectedLeadId || appointment) return;
    getLead(preselectedLeadId).then(({ lead }) => {
      setSelectedLead(lead);
    }).catch(() => {
      // If fetch fails, leave selectedLead as null; form still has the id
    });
  }, [preselectedLeadId, appointment]);

  // The host sheet's header shows the picked patient (create mode).
  useEffect(() => {
    onLeadChange?.(selectedLead);
  }, [selectedLead, onLeadChange]);

  // Unsaved-changes guard: RHF dirtiness plus the two pieces of state kept
  // outside the form (intent + room), compared against what we seeded.
  const isDirty = form.formState.isDirty;
  const seededTypeRef = useRef<AppointmentType>(effectiveDefaultType);
  const seededRoomRef = useRef<string>(preselectedRoomId ?? "");
  useEffect(() => {
    onDirtyChange?.(
      isDirty ||
        selectedType !== seededTypeRef.current ||
        selectedRoomId !== seededRoomRef.current
    );
  }, [isDirty, selectedType, selectedRoomId, onDirtyChange]);

  // Visits Redesign: load the selected lead's visits + purpose map (flag-gated).
  const watchedLeadId = form.watch("lead_id");
  const watchedDate = form.watch("scheduled_date");
  const watchedVisitId = form.watch("clinical_visit_id");

  /* ── Phase 82: care-team prefill (D-3 — prefill, never lock) ───────────── */

  const careTeamEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.care_team_enabled ?? false
  );
  const watchedUserId = form.watch("user_id");
  const careTeam = selectedLead?.care_team;
  const careDoctorId = careTeam?.doctor_id ?? null;

  /**
   * Default the assignee to the patient's care-team doctor so visits 2, 3 and 4
   * land with the same clinician the desk chose the first time.
   *
   * Deliberately soft (D-3). Three things outrank the suggestion and are
   * checked before it applies:
   *   - EDIT mode — a saved appointment's assignee is a decision already made.
   *   - `preselectedUserId` — dragging on a doctor's calendar column IS the
   *     choice of doctor; overriding it would fight the gesture.
   *   - a value we did not put there ourselves — tracked via
   *     autoAppliedDoctorRef, exactly like the visit suggestion above.
   *
   * P-10: agents never see this picker (the server forces self-assignment), so
   * the whole effect no-ops for them rather than sending a user_id that would
   * be overwritten anyway.
   *
   * Only applies to VISITS: a lead call is not clinical work and has no reason
   * to route to the care-team doctor.
   */
  useEffect(() => {
    if (isEditing || !careTeamEnabled || isAgent) return;
    if (preselectedUserId) return;
    if (eventTypesEnabled && selectedType !== "clinic_visit") return;
    if (!careDoctorId) return;

    const suggestion = String(careDoctorId);
    const current = form.getValues("user_id");

    // Only overwrite an untouched default or our own earlier suggestion.
    const ourOwn = autoAppliedDoctorRef.current !== null && current === autoAppliedDoctorRef.current;
    const untouched = !current || current === String(currentUser?.id ?? "");
    if (!ourOwn && !untouched) return;

    // Never set a value the picker cannot render — an inactive or
    // out-of-population doctor would leave the Select blank and the desk
    // wondering what it picked.
    if (!agents.some((a) => String(a.id) === suggestion)) return;

    form.setValue("user_id", suggestion);
    autoAppliedDoctorRef.current = suggestion;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, careTeamEnabled, isAgent, preselectedUserId, eventTypesEnabled, selectedType, careDoctorId, agents]);

  /**
   * The drift warning (D-3): the booking is going ahead with someone other than
   * this patient's care-team doctor. Never blocks — a hard lock would stop the
   * desk booking at all the day the assigned doctor is off sick. It exists so
   * the deviation is visible and deliberate rather than silent.
   */
  const careTeamDrift = Boolean(
    careTeamEnabled &&
      !isAgent &&
      careDoctorId &&
      watchedUserId &&
      String(watchedUserId) !== String(careDoctorId) &&
      (!eventTypesEnabled || selectedType === "clinic_visit")
  );

  const careDoctorName =
    careTeam?.doctor?.full_name ??
    agents.find((a) => String(a.id) === String(careDoctorId))?.full_name ??
    null;

  /* ── Phase 89: the step's own doctor (prefill + warn, like the care team) ── */

  const assignmentEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.treatment_assignment_enabled ?? false
  );

  /**
   * Booking FOR a treatment step (the desk board's Book on a step row):
   * default the assignee to the doctor that treatment is assigned to. Same
   * guards as the care-team prefill above, and it runs after it — a step's
   * own doctor is the more specific signal than the patient-level team, so
   * when both exist this one wins.
   */
  useEffect(() => {
    if (isEditing || !assignmentEnabled || isAgent) return;
    if (preselectedUserId) return;
    if (!preselectedStageIds?.length || !preselectedStageDoctorId) return;
    if (eventTypesEnabled && selectedType !== "clinic_visit") return;

    const suggestion = String(preselectedStageDoctorId);
    const current = form.getValues("user_id");
    const ourOwn = autoAppliedDoctorRef.current !== null && current === autoAppliedDoctorRef.current;
    const untouched = !current || current === String(currentUser?.id ?? "");
    if (!ourOwn && !untouched) return;
    if (!agents.some((a) => String(a.id) === suggestion)) return;

    form.setValue("user_id", suggestion);
    autoAppliedDoctorRef.current = suggestion;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, assignmentEnabled, isAgent, preselectedUserId, preselectedStageIds, preselectedStageDoctorId, eventTypesEnabled, selectedType, agents]);

  const stageDoctorDrift = Boolean(
    assignmentEnabled &&
      !isAgent &&
      preselectedStageIds?.length &&
      preselectedStageDoctorId &&
      watchedUserId &&
      String(watchedUserId) !== String(preselectedStageDoctorId) &&
      (!eventTypesEnabled || selectedType === "clinic_visit")
  );

  const stageDoctorName =
    agents.find((a) => String(a.id) === String(preselectedStageDoctorId))?.full_name ?? null;

  // Full visit-state reset — a lead change must never carry a stale
  // cross-lead clinical_visit_id (the backend would 422 on an unrendered field).
  const resetVisitState = () => {
    form.setValue("clinical_visit_id", "");
    setVisitMode("existing");
    setUserChoseVisit(false);
    setSuggestionDismissed(false);
    setNewVisitPurpose("");
    setLeadVisits([]);
    autoAppliedVisitRef.current = null;
  };

  useEffect(() => {
    if (!visitFieldEnabled || !watchedLeadId) return;
    let cancelled = false;
    Promise.all([getLeadVisits(watchedLeadId), getVisitPurposes()])
      .then(([visits, map]) => {
        if (cancelled) return;
        setLeadVisits(visits);
        setVisitPurposesMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visitFieldEnabled, watchedLeadId]);

  // Visit Purpose v2: visits are normally born from the call center's trips —
  // the inline builder survives ONLY as an escape hatch for local walk-in
  // patients (leads with no visits at all), and it asks purpose only (the
  // number is derived server-side).
  const showNewVisitEscapeHatch = leadVisits.length === 0;

  // Auto-suggestion (create mode only): a visit id inferred from the chosen
  // date, never overriding an explicit selection. Kept as a memo so it never
  // calls setState from an effect.
  const suggestedVisitId = useMemo(() => {
    if (
      !visitFieldEnabled ||
      isEditing ||
      userChoseVisit ||
      suggestionDismissed ||
      visitMode === "new"
    ) {
      return null;
    }
    // No date bail-out: the single-visit tier links the lead's only visit
    // before a date is even picked (call-centre-created visits, 260819).
    return computeSuggestedVisit(watchedDate ?? "", leadVisits);
  }, [
    visitFieldEnabled,
    isEditing,
    userChoseVisit,
    suggestionDismissed,
    visitMode,
    watchedDate,
    leadVisits,
  ]);

  useEffect(() => {
    if (suggestedVisitId) {
      form.setValue("clinical_visit_id", suggestedVisitId);
      autoAppliedVisitRef.current = suggestedVisitId;
    } else if (
      autoAppliedVisitRef.current &&
      !userChoseVisit &&
      form.getValues("clinical_visit_id") === autoAppliedVisitRef.current
    ) {
      form.setValue("clinical_visit_id", "");
      autoAppliedVisitRef.current = null;
    }
  }, [suggestedVisitId, userChoseVisit, form]);

  const suggestedVisit = suggestedVisitId
    ? leadVisits.find((v) => v.id === suggestedVisitId)
    : undefined;

  const visitPurposeLabel = (key: string | null | undefined): string => {
    if (!key) return tv("tab.noPurpose");
    return tv.has(`purposes.${key}`) ? tv(`purposes.${key}`) : key;
  };

  // Derived ordinals can exceed the 4 hard-coded i18n keys.
  const visitNumberLabel = (n: number): string =>
    tv.has(`visit.${n}`) ? tv(`visit.${n}`) : tv("visit.generic", { number: n });

  const formatVisitDate = (visit: ClinicalVisit): string => {
    const from = visit.date_range?.from ?? visit.occurred_on;
    const to = visit.date_range?.to ?? visit.occurred_on;
    if (!from) return "—";
    const fromLabel = format.dateTime(new Date(from), { month: "short", day: "numeric" });
    if (!to || to === from) return fromLabel;
    const toLabel = format.dateTime(new Date(to), { month: "short", day: "numeric" });
    return `${fromLabel} – ${toLabel}`;
  };

  const visitSelectValue =
    visitMode === "new" ? NEW_VISIT : watchedVisitId || NO_VISIT;

  const handleVisitSelect = (value: string) => {
    setUserChoseVisit(true);
    setSuggestionDismissed(true);
    if (value === NO_VISIT) {
      setVisitMode("existing");
      form.setValue("clinical_visit_id", "");
    } else if (value === NEW_VISIT) {
      setVisitMode("new");
      form.setValue("clinical_visit_id", "");
      setNewVisitPurpose("");
    } else {
      setVisitMode("existing");
      form.setValue("clinical_visit_id", value);
    }
  };

  // Suggestion-ordered purpose list for the escape-hatch builder: the lead has
  // zero visits, so the typical first-visit purposes lead the list — everything
  // stays selectable (the per-number map is a soft suggestion only).
  const newVisitPurposeOptions = useMemo(() => {
    const all = Array.from(new Set(Object.values(visitPurposesMap).flat()));
    const suggested = visitPurposesMap["1"] ?? [];
    return [...suggested, ...all.filter((p) => !suggested.includes(p))];
  }, [visitPurposesMap]);

  // Fields whose FormField (and thus FormMessage) is actually MOUNTED this
  // render — mirrors the JSX conditions below. Backend 422s on anything else
  // (incl. flag-hidden fields) must be toasted or they fail silently.
  const renderedErrorFields = new Set([
    "lead_id",
    "title",
    "description",
    "scheduled_date",
    "scheduled_time",
    ...(!hideDurationEnabled && !isCallType ? ["duration_minutes"] : []),
    ...(categoryEnabled ? ["category"] : []),
    ...(!hideAssigneeEnabled && !isAgent ? ["user_id"] : []),
    ...(showCoDoctors ? ["co_doctor_ids"] : []),
  ]);

  // The main doctor never doubles as a co-doctor (the server drops them too).
  const coDoctorIdsFor = (values: AppointmentFormValues): number[] =>
    (values.co_doctor_ids ?? []).filter((id) => id !== values.user_id).map(Number);

  const onSubmit = async (values: AppointmentFormValues) => {
    setIsSubmitting(true);
    try {
      // Combine date and time into ISO string
      const scheduledAt = new Date(`${values.scheduled_date}T${values.scheduled_time}`).toISOString();

      // Visits Redesign: resolve the visit link — create the escape-hatch
      // visit first (purpose only, number derived server-side), so we never
      // leave the appointment unlinked.
      let clinicalVisitId: string | null = values.clinical_visit_id || null;
      if (visitFieldEnabled && !isCallType && visitMode === "new") {
        if (!newVisitPurpose) {
          toast.error(t("appointmentForm.visit.newIncomplete"));
          setIsSubmitting(false);
          return;
        }
        try {
          const createdVisit = await createLeadVisit(values.lead_id, {
            purpose: newVisitPurpose,
          });
          clinicalVisitId = createdVisit.id;
          // Commit the created visit BEFORE the appointment save: if the save
          // fails, a retry links the now-existing visit instead of creating a
          // duplicate row.
          setLeadVisits((prev) => [...prev, createdVisit]);
          setVisitMode("existing");
          form.setValue("clinical_visit_id", createdVisit.id);
        } catch {
          toast.error(t("appointmentForm.visit.createFailed"));
          setIsSubmitting(false);
          return;
        }
      }

      if (isEditing) {
        const updateData: UpdateAppointmentData & { room_id?: string | null } = {
          lead_id: values.lead_id,
          ...(values.title?.trim() ? { title: values.title.trim() } : {}),
          description: values.description,
          scheduled_at: scheduledAt,
          // Calls carry no duration input — omit so the backend applies its
          // 30-min call default (create) / keeps the stored value (update).
          ...(isCallType ? {} : { duration_minutes: values.duration_minutes }),
          // Hidden category UI ⇒ omit the field — the form's baked default
          // ('klea') 422s on clinics with a custom config list; the server
          // defaults absent values to the clinic list's first entry.
          ...(categoryEnabled ? { category: values.category } : {}),
          // Agents self-assign server-side — never send user_id for them.
          // Hidden picker ⇒ omit too: the server defaults an absent assignee
          // to the booking user on create and keeps the saved one on update.
          ...(isAgent || hideAssigneeEnabled ? {} : { user_id: values.user_id }),
          // Only when the field was shown; a call keeps no co-doctors.
          ...(showCoDoctors ? { co_doctor_ids: coDoctorIdsFor(values) } : {}),
          ...(coDoctorsEnabled && isCallType ? { co_doctor_ids: [] } : {}),
          // Call intent hides the visit picker — don't carry a stale visit link.
          ...(visitFieldEnabled && !isCallType
            ? { clinical_visit_id: clinicalVisitId }
            : {}),
          // Rooms: only touch room_id when the picker was shown ("" clears to
          // unassigned); switching an appointment to Call detaches its room.
          ...(showRoomPicker ? { room_id: selectedRoomId || null } : {}),
          ...(isCallType && roomsEnabled ? { room_id: null } : {}),
          // Calendar redesign (Wave 1): only send type when the flag is on;
          // otherwise the server keeps defaulting it by role (identical to today).
          ...(eventTypesEnabled || typeLocked ? { type: selectedType } : {}),
          ...(overrideReasonRef.current
            ? { override_conflicts: true, override_reason: overrideReasonRef.current }
            : {}),
        };
        const { appointment: saved, warnings } = await updateAppointment(appointment.id, updateData);
        toast.success(t("appointmentForm.toast.updated"));
        // WP9: surface any additive warnings (room over-capacity, blockout
        // restriction) as a persistent-ish toast — rendered generically per
        // message so new warning types need no change here.
        showAppointmentWarnings(warnings, { title: warningTitle });
        onSaved(saved);
      } else {
        // Rooms / Salla: room comes from the visible Visit-intent picker when
        // shown, else from a rooms-column slot click (room_id isn't on the
        // CreateAppointmentData type yet). Absent → the backend auto-assigns
        // the first free room when rooms are on. Call intent never sends one.
        const roomIdToSend = showRoomPicker
          ? selectedRoomId || undefined
          : isCallType
            ? undefined
            : preselectedRoomId;
        // Steps ride along only for the patient they belong to, and only on a
        // visit — a call books no treatment step.
        const stageIdsToSend =
          preselectedStageIds?.length &&
          values.lead_id === preselectedLeadId &&
          !isCallType
            ? preselectedStageIds
            : undefined;
        const createData: CreateAppointmentData & { room_id?: string; stage_ids?: string[] } = {
          lead_id: values.lead_id,
          title: values.title?.trim() ?? "",
          description: values.description,
          scheduled_at: scheduledAt,
          // Calls carry no duration input — omit so the backend applies its
          // 30-min call default (create) / keeps the stored value (update).
          ...(isCallType ? {} : { duration_minutes: values.duration_minutes }),
          // Hidden category UI ⇒ omit the field — the form's baked default
          // ('klea') 422s on clinics with a custom config list; the server
          // defaults absent values to the clinic list's first entry.
          ...(categoryEnabled ? { category: values.category } : {}),
          // Agents self-assign server-side — never send user_id for them.
          // Hidden picker ⇒ omit too: the server defaults an absent assignee
          // to the booking user on create and keeps the saved one on update.
          ...(isAgent || hideAssigneeEnabled ? {} : { user_id: values.user_id }),
          ...(showCoDoctors && coDoctorIdsFor(values).length > 0
            ? { co_doctor_ids: coDoctorIdsFor(values) }
            : {}),
          // Call intent hides the visit picker — don't carry a stale visit link.
          ...(visitFieldEnabled && !isCallType
            ? { clinical_visit_id: clinicalVisitId }
            : {}),
          ...(roomIdToSend ? { room_id: roomIdToSend } : {}),
          ...(stageIdsToSend ? { stage_ids: stageIdsToSend } : {}),
          // Calendar redesign (Wave 1): only send type when the flag is on;
          // otherwise the server keeps defaulting it by role (identical to today).
          ...(eventTypesEnabled || typeLocked ? { type: selectedType } : {}),
          ...(overrideReasonRef.current
            ? { override_conflicts: true, override_reason: overrideReasonRef.current }
            : {}),
        };
        const { appointment: saved, warnings } = await createAppointment(createData);
        toast.success(t("appointmentForm.toast.created"));
        showAppointmentWarnings(warnings, { title: warningTitle });
        onSaved(saved);
      }
    } catch (error: unknown) {
      const conflictPayload = getConflictPayload(error);
      if (conflictPayload) {
        setConflict(conflictPayload);
        return;
      }
      const err = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      if (err.response?.data?.errors) {
        const entries = Object.entries(err.response.data.errors);
        entries.forEach(([field, messages]) => {
          form.setError(field as keyof AppointmentFormValues, {
            message: messages[0],
          });
        });
        // Fields without a rendered FormField (clinical_visit_id at minimum)
        // would fail silently — surface the first such message as a toast.
        const unrendered = entries.find(([field]) => !renderedErrorFields.has(field));
        if (unrendered) {
          toast.error(unrendered[1][0]);
        }
      } else {
        toast.error(err.response?.data?.message || t("appointmentForm.toast.saveFailed"));
      }
    } finally {
      overrideReasonRef.current = null;
      setIsSubmitting(false);
    }
  };

  // Phase 45 (D-10): picker returns LeadReference (4 fields); fetch the full
  // Lead resource so downstream reads (selectedLead.full_name, etc.) still work.
  const handleLeadSelect = async (lead: LeadReference) => {
    if (lead.id !== form.getValues("lead_id")) {
      resetVisitState();
    }
    form.setValue("lead_id", lead.id);
    setLeadSearchOpen(false);
    setLeadSearchQuery("");
    try {
      const { lead: fullLead } = await getLead(lead.id);
      setSelectedLead(fullLead);
    } catch (error) {
      console.error("Failed to load selected lead detail", error);
      // Fallback: synthesize a minimal Lead shape from the reference payload
      // so the selected state still renders a name. Missing fields are tolerated
      // by the downstream UI (phone + name only are required for the chip display).
      setSelectedLead({
        ...(lead as unknown as Lead),
        full_name: `${lead.first_name} ${lead.last_name}`,
      } as Lead);
    }
  };

  // Close lead dropdown when clicking outside the picker
  useEffect(() => {
    if (!leadSearchOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (leadPickerRef.current && !leadPickerRef.current.contains(e.target as Node)) {
        setLeadSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [leadSearchOpen]);

  const handleQuickCreateLead = async () => {
    if (!quickForm.first_name || !quickForm.last_name || (!quickClientPhoneOptional && !quickForm.phone)) {
      toast.error(
        t(
          quickClientPhoneOptional
            ? "appointmentForm.toast.quickCreateNameRequired"
            : "appointmentForm.toast.quickCreateRequired"
        )
      );
      return;
    }
    setIsCreatingLead(true);
    try {
      const newLead = await createLead({
        first_name: quickForm.first_name,
        last_name: quickForm.last_name,
        phone: quickForm.phone,
        email: quickForm.email || undefined,
      });
      setLeads((prev) => [newLead, ...prev]);
      setSelectedLead(newLead);
      if (newLead.id !== form.getValues("lead_id")) {
        resetVisitState();
      }
      form.setValue("lead_id", newLead.id);
      setShowQuickCreate(false);
      setQuickForm({ first_name: "", last_name: "", phone: "", email: "" });
      toast.success(t("appointmentForm.toast.clientCreated"));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t("appointmentForm.toast.createClientFailed"));
    } finally {
      setIsCreatingLead(false);
    }
  };

  const patientInitials = (selectedLead?.full_name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  // Footer-band context note (deposit-modal contextual CTA language): the
  // scheduled slot once date + time are both chosen.
  const footerDate = form.watch("scheduled_date");
  const footerTime = form.watch("scheduled_time");
  const footerSlot =
    footerDate && footerTime
      ? `${format.dateTime(new Date(`${footerDate}T00:00:00`), {
          weekday: "short",
          month: "short",
          day: "numeric",
        })} · ${footerTime}`
      : null;

  // Slot grid inputs — the doctor picker's value is a string id.
  const slotUserId = isAgent ? (currentUser?.id ? Number(currentUser.id) : null) : (watchedUserId ? Number(watchedUserId) : null);
  const watchedDuration = form.watch("duration_minutes");
  const watchedTime = form.watch("scheduled_time");

  return (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
            data-slot="appointment-editor"
          >
            {/* Scrollable body — the sheet's header and footer stay put. */}
            <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-6 py-5">
            {/* Appointment intent (Calendar redesign, Wave 1) — flag-gated
                Visit/Call segmented control. Shown in both create and edit so
                staff can re-type misclassified rows. `internal` is deliberately
                not an option (collapses to Visit above). Call mode hides the
                visit-only sections below. */}
            {eventTypesEnabled && !typeLocked && (
              <div className="flex flex-col gap-1.5">
                <span className={LABEL_CLS}>{t("appointmentForm.type.label")}</span>
                  <ToggleGroup
                    type="single"
                    value={selectedType === "lead_call" ? "lead_call" : "clinic_visit"}
                    onValueChange={(value) => {
                      // Radix emits "" when the active item is re-pressed — ignore
                      // it so the control always keeps exactly one selection.
                      if (value) setSelectedType(value as AppointmentType);
                    }}
                    variant="outline"
                    className="w-full *:flex-1"
                  >
                    <ToggleGroupItem value="clinic_visit" disabled={isSubmitting}>
                      <IconStethoscope className="mr-1.5 size-[15px]" />
                      {t("appointmentForm.type.visit")}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="lead_call" disabled={isSubmitting}>
                      <IconPhone className="mr-1.5 size-[15px]" />
                      {t("appointmentForm.type.call")}
                    </ToggleGroupItem>
                  </ToggleGroup>
              </div>
            )}

            {/* Lead Selection */}
            <FormField
              control={form.control}
              name="lead_id"
              render={() => (
                <FormItem className="flex flex-col">
                  <FormLabel className={LABEL_CLS}>{t("appointmentForm.leadLabel")}</FormLabel>
                  <FormControl>
                    <div ref={leadPickerRef} className="relative">
                      {selectedLead ? (
                        <div className="flex items-center justify-between rounded-[10px] border bg-muted/40 px-3 py-2.5">
                          <span className="flex min-w-0 items-center gap-2">
                            <Avatar className="size-[22px]">
                              <AvatarFallback className="bg-primary text-[9px] font-semibold text-primary-foreground">
                                {patientInitials}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate text-[13.5px] font-medium">
                              {selectedLead.full_name}
                            </span>
                          </span>
                          <button
                            type="button"
                            aria-label={t("appointmentForm.clearLeadSelection")}
                            className="ml-2 shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setSelectedLead(null);
                              form.setValue("lead_id", "");
                              setLeadSearchQuery("");
                              resetVisitState();
                            }}
                          >
                            <IconX className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder={t("appointmentForm.searchLeadsPlaceholder")}
                            className={cn("pl-9", FIELD_CLS)}
                            value={leadSearchQuery}
                            onChange={(e) => {
                              setLeadSearchQuery(e.target.value);
                              setLeadSearchOpen(true);
                            }}
                            onFocus={() => setLeadSearchOpen(true)}
                          />
                          {isLoadingLeads && (
                            <IconLoader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                          {leadSearchOpen && (leads.length > 0 || isLoadingLeads) && (
                            <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md overflow-hidden">
                              {isLoadingLeads && leads.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-muted-foreground">{t("appointmentForm.searching")}</div>
                              ) : (
                                <div className="max-h-[240px] overflow-y-auto">
                                  {leads.map((lead) => (
                                    <button
                                      key={lead.id}
                                      type="button"
                                      className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent transition-colors"
                                      onMouseDown={(e) => {
                                        // Prevent input blur before click fires
                                        e.preventDefault();
                                        handleLeadSelect(lead);
                                      }}
                                    >
                                      <div className="min-w-0">
                                        {/* Phase 45: LeadReference shape — derive display name from first+last. Email intentionally absent (picker payload only ships id/first_name/last_name/phone). */}
                                        <span className="text-sm font-medium block truncate">{lead.first_name} {lead.last_name}</span>
                                        {lead.phone && (
                                          <span className="text-xs text-muted-foreground">{lead.phone}</span>
                                        )}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </FormControl>

                  {quickClientEnabled && !showQuickCreate && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-1"
                      onClick={() => setShowQuickCreate(true)}
                    >
                      <IconPlus className="mr-1 h-4 w-4" />
                      {t("appointmentForm.newClient")}
                    </Button>
                  )}

                  {quickClientEnabled && showQuickCreate && (
                    <div className="mt-2 space-y-2 rounded-[11px] border bg-muted/40 p-3">
                      <p className={LABEL_CLS}>{t("appointmentForm.quickCreateClient")}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={t("appointmentForm.firstNamePlaceholder")}
                          value={quickForm.first_name}
                          onChange={(e) => setQuickForm((prev) => ({ ...prev, first_name: e.target.value }))}
                        />
                        <Input
                          placeholder={t("appointmentForm.lastNamePlaceholder")}
                          value={quickForm.last_name}
                          onChange={(e) => setQuickForm((prev) => ({ ...prev, last_name: e.target.value }))}
                        />
                      </div>
                      <Input
                        placeholder={t(
                          quickClientPhoneOptional
                            ? "appointmentForm.phoneOptionalPlaceholder"
                            : "appointmentForm.phonePlaceholder"
                        )}
                        value={quickForm.phone}
                        onChange={(e) => setQuickForm((prev) => ({ ...prev, phone: e.target.value }))}
                      />
                      <Input
                        placeholder={t("appointmentForm.emailPlaceholder")}
                        type="email"
                        value={quickForm.email}
                        onChange={(e) => setQuickForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleQuickCreateLead}
                          disabled={isCreatingLead}
                        >
                          {isCreatingLead && <IconLoader2 className="mr-1 h-3 w-3 animate-spin" />}
                          {t("appointmentForm.createAndSelect")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowQuickCreate(false);
                            setQuickForm({ first_name: "", last_name: "", phone: "", email: "" });
                          }}
                        >
                          {tc("cancel")}
                        </Button>
                      </div>
                    </div>
                  )}

                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={LABEL_CLS}>{t("appointmentForm.titleLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("appointmentForm.titlePlaceholder")}
                      className={FIELD_CLS}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category — hidden when feature flag is off. Phase 79 (UI-SPEC
                §6b): config-driven list — rendered as an icon-tile grid
                (Apollo deposit-modal method-grid language) up to 6 entries,
                a Select beyond that. */}
            {categoryEnabled && (
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_CLS}>{t("appointmentForm.categoryLabel")}</FormLabel>
                    {/* Icon-tile grid (deposit-modal method grid) for the
                        config-driven list; falls back to a Select past 6. */}
                    {categories.length <= 6 ? (
                      <FormControl>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {categories.map((key) => {
                            const Icon = categoryIcon(key);
                            const on = field.value === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => field.onChange(key)}
                                disabled={isSubmitting}
                                className={cn(
                                  "flex items-center gap-2.5 rounded-[11px] border px-3 py-2.5 text-left transition-colors",
                                  on
                                    ? "border-primary/50 bg-primary/10 text-foreground"
                                    : "bg-muted/40 text-muted-foreground hover:border-border",
                                )}
                              >
                                <span
                                  className={cn(
                                    "grid size-7 shrink-0 place-items-center rounded-lg",
                                    on
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted text-muted-foreground",
                                  )}
                                >
                                  <Icon className="size-[15px]" />
                                </span>
                                <span
                                  className={cn(
                                    "truncate text-[13px]",
                                    on ? "font-semibold" : "font-medium",
                                  )}
                                >
                                  {getCategoryLabel(t, key)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </FormControl>
                    ) : (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className={cn("w-full", FIELD_CLS)}>
                            <SelectValue placeholder={t("appointmentForm.categoryLabel")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((key) => (
                            <SelectItem key={key} value={key}>
                              {getCategoryLabel(t, key)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Visit (Visits Redesign) — flag-gated, lead-scoped. Groups the
                appointment under a clinical visit; "+ New visit" builds one
                inline and creates it on submit. Hidden for Call intent. */}
            {visitFieldEnabled && selectedLead && !isCallType && (
              <FormItem>
                <FormLabel className={LABEL_CLS}>{t("appointmentForm.visit.label")}</FormLabel>
                <Select value={visitSelectValue} onValueChange={handleVisitSelect}>
                  <FormControl>
                    <SelectTrigger className={cn("w-full", FIELD_CLS)}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={NO_VISIT}>
                      {t("appointmentForm.visit.none")}
                    </SelectItem>
                    {leadVisits.map((visit) => (
                      <SelectItem key={visit.id} value={visit.id}>
                        {`${visitNumberLabel(visit.visit_number)} — ${visitPurposeLabel(visit.purpose)} · ${formatVisitDate(visit)}`}
                      </SelectItem>
                    ))}
                    {showNewVisitEscapeHatch && (
                      <SelectItem value={NEW_VISIT}>
                        {t("appointmentForm.visit.new")}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>

                {suggestedVisit && !userChoseVisit && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {t("appointmentForm.visit.suggested", {
                        visit: visitNumberLabel(suggestedVisit.visit_number),
                      })}
                    </span>
                    <button
                      type="button"
                      aria-label={t("appointmentForm.visit.dismissSuggestion")}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setSuggestionDismissed(true);
                        form.setValue("clinical_visit_id", "");
                      }}
                    >
                      <IconX className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {visitMode === "new" && (
                  <div className="mt-2">
                    <Select
                      value={newVisitPurpose}
                      onValueChange={setNewVisitPurpose}
                    >
                      <SelectTrigger className={cn("w-full min-w-0", FIELD_CLS)}>
                        <SelectValue placeholder={t("appointmentForm.visit.purposePlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {newVisitPurposeOptions.map((p) => (
                          <SelectItem key={p} value={p}>
                            {visitPurposeLabel(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </FormItem>
            )}

            {/* Assigned Agent */}
            {!hideAssigneeEnabled && !isAgent && (
              <FormField
                control={form.control}
                name="user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_CLS}>
                      {assigneesAreDoctors
                        ? t("appointmentForm.doctorLabel")
                        : t("appointmentForm.assignedToLabel")}
                    </FormLabel>
                    {/* Radix calls onValueChange("") when the value is set
                        before its option exists — an edit form resets to the
                        saved doctor before the doctor list has loaded, which
                        blanked the Doctor on every edit (and the save then
                        422'd). No option here is "", so a user never sends it. */}
                    <Select
                      onValueChange={(value) => {
                        if (value) field.onChange(value);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className={cn("w-full", FIELD_CLS)}>
                          <SelectValue
                            placeholder={
                              assigneesAreDoctors
                                ? t("appointmentForm.selectDoctorPlaceholder")
                                : t("appointmentForm.selectAgentPlaceholder")
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {agents.map((agent) => {
                          // Doctor Profiles: enrich options for doctors that have
                          // a profile (color dot + muted specialty). User.id is a
                          // string; the map is keyed by numeric doctor id.
                          const meta = doctorMeta.get(Number(agent.id));
                          return (
                            <SelectItem key={agent.id} value={String(agent.id)}>
                              {meta ? (
                                <span className="flex items-center gap-2">
                                  {meta.color && (
                                    <span
                                      aria-hidden
                                      className="h-2 w-2 shrink-0 rounded-full"
                                      style={{ backgroundColor: meta.color }}
                                    />
                                  )}
                                  <span>{agent.full_name}</span>
                                  {meta.specialty && (
                                    <span className="text-xs text-muted-foreground">
                                      {meta.specialty}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                agent.full_name
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {/* Phase 82 (D-3) — prefill, never lock. This says the
                        booking is going ahead off the patient's care team; it
                        does not stop it. A hard lock would leave the desk
                        unable to book at all the day the assigned doctor is
                        off sick. */}
                    {careTeamDrift && careDoctorName && (
                      <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-500">
                        <IconAlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>
                          {t("appointmentForm.careTeam.offTeam", { name: careDoctorName })}
                        </span>
                      </p>
                    )}
                    {/* Phase 89 — same posture for the step's own doctor. */}
                    {stageDoctorDrift && stageDoctorName && (
                      <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-500">
                        <IconAlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>
                          {t("appointmentForm.treatmentDoctor.offDoctor", { name: stageDoctorName })}
                        </span>
                      </p>
                    )}
                    {/* The care team's other half, so the desk sees who this
                        patient belongs to without leaving the dialog (R-3). */}
                    {careTeamEnabled && !isAgent && careTeam?.prosthetist?.full_name && (
                      <p className="mt-1 text-[11px] text-text-dim">
                        {t("appointmentForm.careTeam.prosthetist", {
                          name: careTeam.prosthetist.full_name,
                        })}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* ArtDent 261005 — the other doctors working this visit. Each
                one sees it in their own column and is booked out for it. */}
            {showCoDoctors && (
              <FormField
                control={form.control}
                name="co_doctor_ids"
                render={({ field }) => {
                  const selected = field.value ?? [];
                  const others = agents.filter((agent) => String(agent.id) !== watchedUserId);
                  return (
                    <FormItem>
                      <FormLabel className={LABEL_CLS}>{t("appointmentForm.coDoctors.label")}</FormLabel>
                      {others.length === 0 ? (
                        <p className="text-[11px] text-text-dim">{t("appointmentForm.coDoctors.none")}</p>
                      ) : (
                        <div
                          role="group"
                          aria-label={t("appointmentForm.coDoctors.label")}
                          className="flex flex-wrap gap-1.5"
                        >
                          {others.map((agent) => {
                            const id = String(agent.id);
                            const on = selected.includes(id);
                            const color = doctorMeta.get(Number(agent.id))?.color;
                            return (
                              <button
                                key={id}
                                type="button"
                                aria-pressed={on}
                                disabled={isSubmitting}
                                onClick={() =>
                                  field.onChange(on ? selected.filter((v) => v !== id) : [...selected, id])
                                }
                                className={cn(
                                  "inline-flex h-8 items-center gap-1.5 rounded-[8px] border px-2.5 text-[12.5px] font-medium transition-colors",
                                  on
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "bg-muted/40 text-foreground hover:border-primary/50"
                                )}
                              >
                                {on ? (
                                  <IconCheck aria-hidden className="size-3.5 shrink-0" />
                                ) : color ? (
                                  <span
                                    aria-hidden
                                    className="size-2 shrink-0 rounded-full"
                                    style={{ backgroundColor: color }}
                                  />
                                ) : null}
                                {agent.full_name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            {/* Rooms / Salla: visible room picker for Visit intent only.
                "" = automatic (create: backend auto-assigns; edit: unassigned). */}
            {showRoomPicker && (
              <div className="flex flex-col gap-1.5">
                <span className={LABEL_CLS}>{t("appointmentForm.room.label")}</span>
                <Select
                  value={selectedRoomId || "auto"}
                  onValueChange={(v) => setSelectedRoomId(v === "auto" ? "" : v)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className={cn("w-full", FIELD_CLS)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      {t("appointmentForm.room.automatic")}
                    </SelectItem>
                    {rooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        <span className="flex items-center gap-2">
                          {room.color && (
                            <span
                              aria-hidden
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: room.color }}
                            />
                          )}
                          <span>{room.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="scheduled_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_CLS}>{t("appointmentForm.dateLabel")}</FormLabel>
                    <FormControl>
                      <Input type="date" className={FIELD_CLS} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="scheduled_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_CLS}>{t("appointmentForm.timeLabel")}</FormLabel>
                    <FormControl>
                      <Input type="time" className={FIELD_CLS} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Free times for the selected staff member and appointment type. */}
            {watchedDate && (
              <SlotGrid
                date={watchedDate}
                doctorId={isCallType ? null : slotUserId}
                userId={isCallType ? slotUserId : null}
                appointmentType={isCallType ? "lead_call" : "clinic_visit"}
                durationMinutes={isCallType ? (appointment?.duration_minutes ?? 30) : (watchedDuration || 60)}
                roomId={showRoomPicker ? selectedRoomId || null : null}
                excludeAppointmentId={appointment?.id}
                value={watchedTime || null}
                disabled={isSubmitting}
                onSelect={(time) =>
                  form.setValue("scheduled_time", time, { shouldDirty: true, shouldValidate: true })
                }
              />
            )}

            {/* 84-05 / 86-01: a blocked booking's clashes, inline under the
                time that caused them, with the override reason in place. */}
            {conflict && (
              <ConflictOverridePanel
                conflict={conflict}
                busy={isSubmitting}
                onDismiss={() => setConflict(null)}
                onConfirm={(reason) => {
                  setConflict(null);
                  overrideReasonRef.current = reason;
                  void form.handleSubmit(onSubmit)();
                }}
              />
            )}

            {/* Duration */}
            {/* Calls have no duration input — 30 min by backend convention. */}
            {!hideDurationEnabled && !isCallType && (
              <FormField
                control={form.control}
                name="duration_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={LABEL_CLS}>{t("appointmentForm.durationLabel")}</FormLabel>
                    {/* Quick-chip row (deposit-modal %-chips language). */}
                    <FormControl>
                      <div className="flex flex-wrap gap-1.5">
                        {DURATION_OPTIONS.map((opt) => {
                          const on = field.value === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => field.onChange(opt.value)}
                              disabled={isSubmitting}
                              className={cn(
                                "rounded-full border px-2.5 py-1 text-[11.5px] font-medium tabular-nums transition-colors",
                                on
                                  ? "border-primary/50 bg-primary/10 text-primary"
                                  : "text-muted-foreground hover:border-primary/50 hover:text-primary",
                              )}
                            >
                              {t(opt.labelKey)}
                            </button>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={LABEL_CLS}>{t("appointmentForm.notesLabel")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("appointmentForm.notesPlaceholder")}
                      className={cn("min-h-14 resize-none", FIELD_CLS)}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            </div>

            {/* Footer band (deposit-modal) — contextual slot note + CTA */}
            <div className="flex shrink-0 items-center gap-2.5 border-t bg-muted/40 px-6 py-3.5">
              <span className="truncate text-[11.5px] tabular-nums text-muted-foreground">
                {footerSlot ?? t("appointmentForm.footerNote")}
              </span>
              <span className="flex-1" />
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {tc("cancel")}
              </Button>
              {canSubmit && (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && (
                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? t("appointmentForm.update") : t("appointmentForm.create")}
                </Button>
              )}
            </div>
          </form>
        </Form>
  );
}
